import { useEffect, useRef, memo } from "react";
import { withFeature } from "flagged";
import { trackEvent } from "@amplio-common/analytics";
import { NEW_VERSION_CHECK_INTERVAL } from "@app/constants";
import { toast } from "react-toastify";
import NewVersionToast from "../common/components/NewVersionToast";
import * as workerTimers from "worker-timers";
import { verifyVersion } from "@app/routes/auth/api";

/*
  a component to that runs an interval and checks if we deployed a new version in the background
  i.e. - the user had the app open - but we deployed a new main.js - we're utilizing the fact that each build
  generates new build.<UUID>.js to distnguish if there's a new version.
*/

const NewVersionCheck = () => {
  const intervalCheckRef = useRef(null);

  const compareVersions = async () => {
    const { response } = await verifyVersion();
    if (!process.env.NX_REACT_APP_BITBUCKET_COMMIT) return;
    if (
      !!response.version
      && response.version !== process.env.NX_REACT_APP_BITBUCKET_COMMIT
    ) return true;
    return false;
  };

  useEffect(async () => {
    const clearIntervalCheck = () => {
      intervalCheckRef.current && workerTimers.clearInterval(intervalCheckRef.current);
      intervalCheckRef.current = null;
    };
    intervalCheckRef.current = workerTimers.setInterval(async () => {
      if (!intervalCheckRef.current) return;
      const _hasUpdate = await compareVersions();
      if (!_hasUpdate) return;
      toast.info(<NewVersionToast />, {
        position: "bottom-left",
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        className: "non-clickable",
        toastId: "new-version-check-toast",
      });
      trackEvent("new_version_found");
      clearIntervalCheck(); // show only once
    }, NEW_VERSION_CHECK_INTERVAL);
    return clearIntervalCheck;
  }, []);

  return null;
};

export default withFeature("NEW_VERSION_CHECK")(memo(NewVersionCheck));
